/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, Inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Data, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { EnvironmentRef } from '../environments/interfaces';
import { AppFactory } from './app.factory';
import { App, AppState } from './definitions/app.interface';
import { expEnvironmentRef } from './injection-tokens/environment.injection-token';
import { LoggerService } from './services/logger/logger.service';
import { Icons, skipAuthUrls } from './services/shared/shared-enums';
import { KngLoaderState } from 'kinetic-angular';
import { LocalStorageService } from './services/local-storage/local-storage.service';
import { SharedUtilService } from './services/shared/shared-util.service';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { ShellService } from './components/shell/shell.service';
import { tmhCustomerSupportRoleId } from './services/shared/shared-constants';
import { TranslateService } from '@ngx-translate/core';
declare global {
	interface Window {
		_walkmeConfig: {smartLoad: boolean};
	}
}

export let browserRefresh = false;

@Component({
	selector: 'tmhna-portal-root',
	templateUrl: './template/app.component.html',
	styleUrls: ['./template/app.component.scss']
})
export class AppComponent implements OnInit, App
{
	private state$: BehaviorSubject<AppState> = new BehaviorSubject<AppState>(
		AppFactory.buildAppState()
	);
	isLandingPage!: boolean;
	get state(): AppState
	{
		return this.state$.value;
	}
	showPageLoader: boolean = false;
	loaderState: KngLoaderState = {
		imagePath: Icons.loaderOrange
	};
  	unreadMessageCount: number = 0;
	unreadEventInitiationTry: number = 0;
	constructor(
    @Inject(expEnvironmentRef) readonly environmentRef: EnvironmentRef,
    private loggerService: LoggerService,
    public router: Router,
    public localStorageService: LocalStorageService,
    private titleService: Title,
	private readonly shellService: ShellService,
	private readonly activatedRoute: ActivatedRoute,
	private readonly msalService: MsalService,
	private readonly sharedUtilService : SharedUtilService,
	private translate: TranslateService
	)
	{
		this.zendeskChatBot();
		router.canceledNavigationResolution = 'computed';
		this.router.events.subscribe((event) =>
		{
			if (event instanceof NavigationStart)
			{
				this.setTranslation(event);
				this.isCancelButtonError(event, '/auth#error=access_denied');
				browserRefresh = !this.router.navigated;
				if(event.url.includes('autoLogin=true') || event.url.includes('autoLogin=1'))
				{
					localStorage.setItem('inboudUrl', this.removeAutoLoginParam(event.url));
					this.router.navigate(['auth']);
				}
			}
		});
	}

	setTranslation(event: NavigationStart)
	{	
		const lang = this.localStorageService.getLanguage() || 'en';
		this.translate.use(lang);
		if(event.url === '/auth') return;

		if (event.url === '/') 
		{
			this.router.navigate([lang]);
			return;
		}

		const newUrl = event.url.replace(/^\/[a-z]{2}(\/|$)/, `/${lang}$1`);
		if (newUrl !== event.url) 
		{
			this.router.navigate([newUrl]);
			return;
		}
		
	}
	isCancelButtonError(event: NavigationStart, url: string)
	{
		if(event.url.includes(url))
		{
			this.showPageLoader = true;
			setTimeout(() => 
			{
				this.showPageLoader = false;
			}, 500);
			this.localStorageService.clearStorage();
			const lang = this.translate.currentLang || 'en';
			this.router.navigate([lang]);
		}
	}
	removeAutoLoginParam(url: string): string 
	{
  	if (!url.includes('autoLogin=')) return url;
  
		try 
		{
			const urlObj = new URL(url);
			urlObj.searchParams.delete('autoLogin');
			return urlObj.toString();
		} 
		catch 
		{
			let newUrl = url.replace(/\?autoLogin=(true|1)&/, '?');
			newUrl = newUrl.replace(/&autoLogin=(true|1)&/g, '&');
			newUrl = newUrl.replace(/\?autoLogin=(true|1)($|#)/, '$2');
			newUrl = newUrl.replace(/&autoLogin=(true|1)($|#)/, '$2');
			return newUrl;
		}
	}
	checkTokenExpiry()
	{
		const accounts = this.msalService.instance.getAllAccounts();
		if(accounts.length > 0)
		{
			this.msalService.acquireTokenSilent({ account: accounts[0], scopes: [environment.scope.read] }).subscribe((tokenResponse)=>
			{
				const expiry = tokenResponse?.expiresOn || new Date();
				const currentTime = new Date().getTime();
				if(expiry.getTime() <= currentTime)
				{
					this.localStorageService.clearStorage();
				}
			});
		}
	}
	async ngOnInit(): Promise<void>
	{
		this.checkTokenExpiry();
		const currentUrl = this.router['location']?._platformLocation?.location?.href;
		const searchParams = new URLSearchParams(this.router['location']?._platformLocation?.location?.search);
		if (currentUrl !== `${this.router['location']?._platformLocation?.location?.origin}/`)
		{
			if(this.router['location']?._platformLocation?.location?.pathname.includes(skipAuthUrls) ) 
			{
				const originValue = searchParams.get('metadata'); 
				if(originValue)
				{
					localStorage.setItem('queryData', originValue );
					searchParams.delete('metadata');
				}
				this.router.navigate([this.router['location']?._platformLocation?.location?.pathname]);
			}
			else 
			{
				const emailValue = searchParams.get('email');
				if(emailValue) 
				{
					const decodedEmail = decodeURIComponent(atob(emailValue));
					this.checkNewimpersonation(decodedEmail);
					this.localStorageService.setRedirectedUser(decodedEmail);
					searchParams.delete('email');
					this.shellService.switchUserRole({ profileId: tmhCustomerSupportRoleId }, false);
				}
			}
		}
		else
		{
			this.isLandingPage = true;
		}
		this.loadWalkMeScript();
		this.loggerService.log(
			`App initialized @ ${this.environmentRef.env.toUpperCase()}`
		);

		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd)
		).subscribe(() =>
		{
			const route = this.getChild(this.activatedRoute);
			route.data.subscribe(((data: Data) =>
			 {
				this.titleService.setTitle(data['title']);
			}));
		});
		this.router.events.subscribe((event) => 
		{
			if (!(event instanceof NavigationEnd)) 
			{
				return;
			}
			window.scrollTo(0, 0);
		});
		this.getUnreadMessages();		
	}
	getChild(activatedRoute: ActivatedRoute): ActivatedRoute
	{
		if (activatedRoute.firstChild)
		{
			return this.getChild(activatedRoute.firstChild);
		}
		else
		{
			return activatedRoute;
		}

	}

	checkNewimpersonation(email:string)
	{
		const redirectedUser = this.localStorageService.getImpersonateUser();
		const parsedData = JSON.parse(redirectedUser as string);
		if(parsedData?.email as string !== email)
		{
			this.localStorageService.setCustomerToDashboard('false');
			this.sharedUtilService.isCustomerSupportSession$.next(false);
			this.localStorageService.removeImpersonateUser();
			this.localStorageService.setUserName('');
		}
	}
	loadWalkMeScript() 
	{
		const requiredWalkMeScriptDetails = {
			id: 'walkme',
			src: this.environmentRef.appWalkmeUrl,
			type: 'text/javascript'
		};
		this.renderExternalScript(requiredWalkMeScriptDetails);
	}

	zendeskChatBot() 
	{
		const requiredZendeskScriptDetails = {
			id: 'ze-snippet',
			src: this.environmentRef.zendeskChatbotUrl
		};
		this.renderExternalScript(requiredZendeskScriptDetails);
	}

	renderExternalScript(externalScript : any) 
	{
		const script = document.createElement('script');
		script.src = externalScript.src;
		script.id = externalScript.id;
		script.type = 'text/javascript';
		script.async = true;
		document.head.appendChild(script);
	}

	openChatBot() 
	{
		try 
		{
			(window as any)['zE']('messenger', 'open');
			this.unreadMessageCount = 0;
		} 
		catch (err) 
		{
			this.loggerService.log('error in opening the chatbot', err);
		}
		this.getUnreadMessages();
	}
	getUnreadMessages () 
	{
		const that = this;
		try 
		{
		 	(window as any)['zE']('messenger:on', 'unreadMessages', (count: number) =>
			{
				that.unreadEventInitiationTry = 0;
				this.updateCount(count);
		  	});
		}
		catch (err) 
		{
		  	this.unreadEventInitiationTry = this.unreadEventInitiationTry + 1;
		  	if (this.unreadEventInitiationTry < 5) 
			{
				setTimeout(this.getUnreadMessages, 1000);
		  	}
		}
	}

	updateCount(count: number) 
	{
		this.unreadMessageCount = count;
	}

}


